import clsx from 'clsx'
import { KeyboardEvent, ReactNode, useEffect, useRef } from 'react'

export interface TabItem {
    id: string
    label: string
    content: ReactNode
    hidden?: boolean
    eventName?: string
    counter?: number
    /** Rendered after the label, e.g. a count badge styled for the active or inactive state. */
    badge?: ReactNode
}

export interface TabsNavigationProps {
    tabs: TabItem[]
    activeTabId: string
    onTabClick: (tab: TabItem) => void
    tabListRightContent?: ReactNode
    /** Accessible name of the tab list, announced before its tabs. */
    tabListLabel?: string
    className?: string
    /** Spacing and sizing of every tab button, whatever its state. */
    tabClassName?: string
    /**
     * Typography and colour of the selected tab's label. Passed as separate per-state props rather
     * than merged into `tabClassName`, so a consumer can restyle the label without a colour or
     * weight class of theirs competing with the default of the other state.
     */
    activeTabClassName?: string
    /** Typography and colour of an unselected tab's label. */
    inactiveTabClassName?: string
    /** Colour of the selected tab's underline. */
    activeIndicatorClassName?: string
    tabListContainerClassName?: string
    tabListClassName?: string
    tabContentClassName?: string
}

const getTabId = (tabId: string) => `tab-${tabId}`
const getTabPanelId = (tabId: string) => `tabpanel-${tabId}`

/**
 * Reusable tabs component with keyboard navigation and ARIA support.
 * Renders both tab buttons and tab panels with proper accessibility attributes.
 */
const TabsNavigation = ({
    tabs,
    activeTabId,
    onTabClick,
    tabListRightContent,
    tabListLabel,
    className,
    tabClassName = 'px-2 py-3',
    // Every tab reads the same by default; the underline alone says which one is open, so there
    // is no dimmed-out state to tell apart.
    activeTabClassName = 'font-medium text-text-primary',
    inactiveTabClassName = 'font-medium text-text-primary',
    activeIndicatorClassName = 'bg-brand-500',
    tabListContainerClassName,
    tabListClassName,
    tabContentClassName,
}: TabsNavigationProps) => {
    const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
    const visibleTabs = tabs.filter((tab) => !tab.hidden)

    // Focus the active tab when it changes via keyboard
    useEffect(() => {
        const activeButton = tabRefs.current.get(activeTabId)
        if (activeButton && document.activeElement?.getAttribute('role') === 'tab') {
            activeButton.focus()
        }
    }, [activeTabId])

    const handleKeyDown = (e: KeyboardEvent, tabId: string) => {
        const currentIndex = visibleTabs.findIndex((tab) => tab.id === tabId)

        if (e.key === 'ArrowRight') {
            e.preventDefault()
            const nextIndex = (currentIndex + 1) % visibleTabs.length
            onTabClick(visibleTabs[nextIndex])
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault()
            const prevIndex = (currentIndex - 1 + visibleTabs.length) % visibleTabs.length
            onTabClick(visibleTabs[prevIndex])
        } else if (e.key === 'Home') {
            e.preventDefault()
            onTabClick(visibleTabs[0])
        } else if (e.key === 'End') {
            e.preventDefault()
            onTabClick(visibleTabs[visibleTabs.length - 1])
        }
    }

    const activeTab = visibleTabs.find((tab) => tab.id === activeTabId)

    return (
        <div className={className}>
            <div className={clsx('flex items-center', tabListContainerClassName)}>
                <div
                    role="tablist"
                    aria-label={tabListLabel}
                    className={clsx(
                        // `auto`, so the track only appears when the tabs actually outrun their
                        // row; `scroll` reserved one whether or not there was anything to scroll.
                        'flex overflow-x-auto overflow-y-hidden [scrollbar-width:none]',
                        tabListClassName,
                    )}>
                    {visibleTabs.map((tab) => {
                        const isActive = activeTabId === tab.id
                        return (
                            <button
                                key={tab.id}
                                id={getTabId(tab.id)}
                                ref={(el) => {
                                    if (el) {
                                        tabRefs.current.set(tab.id, el)
                                    } else {
                                        tabRefs.current.delete(tab.id)
                                    }
                                }}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                aria-controls={getTabPanelId(tab.id)}
                                tabIndex={isActive ? 0 : -1}
                                onClick={() => onTabClick(tab)}
                                onKeyDown={(e) => handleKeyDown(e, tab.id)}
                                className={clsx(
                                    'group',
                                    'relative inline-block cursor-pointer whitespace-nowrap text-center',
                                    'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500',
                                    isActive ? activeTabClassName : inactiveTabClassName,
                                    tabClassName,
                                )}>
                                {/* A flex row, so the label is centred here rather than by `text-center`. */}
                                <span className="flex items-center justify-center gap-2">
                                    {tab.label}
                                    {tab.badge}
                                    {(tab.counter ?? 0) > 0 && (
                                        <span
                                            className={clsx(
                                                'inline-flex items-center justify-center',
                                                'bg-brand-400 text-white text-typography-text-xs font-bold',
                                                'h-4 min-w-4 px-1 rounded-full',
                                                'leading-[10px]',
                                            )}>
                                            {tab.counter}
                                        </span>
                                    )}
                                </span>
                                <span
                                    className={clsx(
                                        // One background class per state: with both present the stylesheet
                                        // order decides, and bg-transparent hid the active underline.
                                        'pointer-events-none absolute inset-x-0 bottom-0 h-[2px]',
                                        isActive ? activeIndicatorClassName : 'bg-transparent',
                                        'mouse:group-hover:bg-brand-500',
                                        'touch:group-active:bg-brand-500',
                                    )}
                                />
                            </button>
                        )
                    })}
                </div>
                {tabListRightContent}
            </div>
            {activeTab && (
                <div
                    id={getTabPanelId(activeTab.id)}
                    role="tabpanel"
                    aria-labelledby={getTabId(activeTab.id)}
                    className={tabContentClassName}>
                    {activeTab.content}
                </div>
            )}
        </div>
    )
}

export default TabsNavigation
